import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, FileText, TrendingUp, Users, UserCircle, BarChart3, Menu, LogOut, Sun, Moon, Building2, MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: Home },
    { name: 'Publicaciones', href: '/admin/publicaciones', icon: FileText },
    { name: 'Ofertas', href: '/admin/ofertas', icon: TrendingUp },
    { name: 'Mensajes', href: '/admin/mensajes', icon: MessageCircle },
    { name: 'Revendedores', href: '/admin/revendedores', icon: Users },
    { name: 'Usuarios', href: '/admin/usuarios', icon: UserCircle },
    { name: 'Sucursales', href: '/admin/sucursales', icon: Building2 },
    { name: 'Reportes', href: '/admin/reportes', icon: BarChart3 },
  ];

  const NavItems = ({ mobile = false }) => (
    <nav className="flex flex-col gap-1">
      {navigation.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.href || 
          (item.href !== '/' && location.pathname.startsWith(item.href));
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => mobile && setOpen(false)}
            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
              isActive
                ? 'bg-primary/20 text-primary border border-primary/30'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent'
            }`}
            data-testid={`nav-${item.name.toLowerCase()}`}
          >
            <Icon className="h-5 w-5" />
            {item.name}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-card border-r border-border">
        <div className="flex h-16 items-center border-b border-border px-6">
          <Link to="/admin" className="flex items-center justify-center w-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_vehicle-resell/artifacts/1ovezqcl_Logo_amaya.png" 
              alt="Amaya Motors" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <NavItems />
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-background">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 border-b border-border bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              {/* Mobile Menu */}
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 bg-card border-border">
                  <div className="flex h-16 items-center border-b border-slate-800/60 px-6">
                    <Link to="/admin" className="flex items-center justify-center w-full" onClick={() => setOpen(false)}>
                      <img 
                        src="https://customer-assets.emergentagent.com/job_vehicle-resell/artifacts/1ovezqcl_Logo_amaya.png" 
                        alt="Amaya Motors" 
                        className="h-10 w-auto"
                      />
                    </Link>
                  </div>
                  <div className="p-4">
                    <NavItems mobile />
                  </div>
                </SheetContent>
              </Sheet>

              <Link to="/admin" className="lg:hidden flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vehicle-resell/artifacts/1ovezqcl_Logo_amaya.png" 
                  alt="Amaya Motors" 
                  className="h-8 w-auto"
                />
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user?.nombre} <span className="text-muted-foreground/70">({user?.rol})</span>
              </span>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                data-testid="theme-toggle-button"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="gap-2"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Salir</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;